import { NotificationBanner } from '@greystar/designsystem';
import { useAnalytics, useFormattedAddress } from '@greystar/lib';
import { EventProperty } from '@greystar/lib/src/types/property-types';
import { AlertBannerProps } from '@greystar/ui/src/component-props';
import {
  Link as JssLink,
  useSitecoreContext,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { useEffect, useState } from 'react';
import useCookieManager from '../../../hooks/useCookieManager';

const variantMapping: Record<string, string> = {
  'container-yellow-background': 'warning',
  'container-blue-background': 'info',
};

const AlertBanner = (props: AlertBannerProps): JSX.Element => {
  const [isVisible, setIsVisible] = useState<boolean | undefined>(undefined);
  const analytics = useAnalytics();
  const { getCookie, setCookie } = useCookieManager({ expirationHours: 24 });

  const { cta, alertBannerText } = props.fields || {};
  const canDismiss = props.rendering?.params?.canDismiss === '1';
  const dynamicPlaceholderId = props.params?.DynamicPlaceholderId;
  const backgroundColor =
    props.rendering?.params?.backgroundColour &&
    variantMapping[props.rendering.params.backgroundColour]
      ? variantMapping[props.rendering.params.backgroundColour]
      : 'error';

  const context = useSitecoreContext();
  const property: EventProperty = context?.sitecoreContext?.property ?? {};
  const location = property?.location ?? {};
  const property_location = useFormattedAddress(location);

  const bannerKey = 'alertBannerDismissed';

  useEffect(() => {
    const isDismissed = getCookie(bannerKey);
    setIsVisible(!isDismissed);
  }, []);

  const pushDataLayerEvent = (
    event: string,
    additionalData: Partial<{
      state?: string;
      actionType: string;
      property_location?: string;
      property_id?: string;
      property_name?: string;
      module_name?: string;
      inpage_location?: string;
      click_text?: string;
      click_type?: string;
      destination_url?: string;
    }> = {}
  ) => {
    const eventData = {
      event: event ?? 'alert_banner_click',
      action_type: additionalData.actionType,
      state: location?.state ?? '',
      property_location: property_location,
      property_id: property.id ?? '',
      property_name: property.name ?? '',
      module_name: 'alert banner',
      inpage_location: 'top',
      click_text: additionalData.click_text ?? alertBannerText?.value ?? '',
      click_type: additionalData.click_type ?? 'internal',
      destination_url: additionalData.destination_url ?? '',
    };
    analytics.trackEvent(event, eventData);
  };

  const handleDismiss = () => {
    pushDataLayerEvent('alert_banner_click', {
      actionType: 'close',
      click_text: alertBannerText?.value,
    });
    setCookie(bannerKey, 'true');
    setIsVisible(false);
  };

  const handleCtaClick = () => {
    if (cta?.value?.href) {
      pushDataLayerEvent('alert_banner_click', {
        actionType: 'find out more',
        click_text: alertBannerText?.value,
        destination_url: cta.value.href,
        click_type: cta.value.target === '_blank' ? 'external' : 'internal',
      });
    }
  };

  if (isVisible === undefined || !isVisible || !alertBannerText?.value) return <></>;

  return (
    <NotificationBanner
      text={alertBannerText.value}
      cta={cta?.value?.href ? <JssLink field={cta} onClick={handleCtaClick} /> : null}
      canDismiss={canDismiss}
      variant={backgroundColor}
      data-dynamic-placeholder-id={dynamicPlaceholderId}
      onCloseBanner={handleDismiss}
      iconName="Warning"
    />
  );
};

export default withDatasourceCheck()<AlertBannerProps>(AlertBanner);
